import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  InputBase,
  Box,
  IconButton,
  Chip,
  Divider,
  Typography,
} from "@mui/material";
import { Search, CameraAlt, Refresh } from "@mui/icons-material";
import { getScanText, selectAndFocus } from "utils";
import Scanner from "components/Scanner";
import TrackingDetail from "components/TrackingDetail";
import TrackingSummaryItem from "components/TrackingSummaryItem";
import PageShell from "components/bento/PageShell";
import BentoCard from "components/bento/BentoCard";
import { BG_TINT, TEXT_SUBTLE, PURPLE_MAIN, PURPLE_DARK, DIVIDER } from "components/bento/bentoTokens";

const MAX_TRACKING = 10;

const parseInput = (input) =>
  input
    .split(/[,\n;]+/)
    .map((s) => getScanText(s.trim()))
    .filter(Boolean)
    .slice(0, MAX_TRACKING);

const Tracking = () => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [trackingIds, setTrackingIds] = useState([]);

  const handleSearch = (rawValue = inputValue) => {
    const ids = parseInput(rawValue);
    setTrackingIds(ids);
    if (ids.length === 1) setInputValue(ids[0]);
  };

  const parsedCount = parseInput(inputValue).length;
  const isSingle = trackingIds.length === 1;
  const isMultiple = trackingIds.length > 1;

  return (
    <PageShell title={t("trackingPage.title")} subtitle={t("trackingPage.subtitle")}>
      <Box sx={{ maxWidth: 650 }}>
        {/* Search bar */}
        <BentoCard bodySx={{ p: "12px 16px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Search sx={{ color: TEXT_SUBTLE, flexShrink: 0, alignSelf: "flex-start", mt: "8px" }} />
            <InputBase
              id="trackingNumber"
              placeholder={t("trackingPage.searchPlaceholder")}
              fullWidth
              multiline
              maxRows={6}
              autoFocus
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                if (!e.target.value) setTrackingIds([]);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSearch(e.target.value);
                }
              }}
              sx={{ fontSize: "0.95rem" }}
            />
            {parsedCount > 1 && (
              <Chip
                label={parsedCount}
                size="small"
                color="secondary"
                sx={{ height: 22, minWidth: 28, fontSize: "0.75rem", fontWeight: 700 }}
              />
            )}
            <IconButton onClick={() => setOpen(true)} size="small" sx={{ color: "text.secondary" }}>
              <CameraAlt fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => handleSearch()}
              sx={{
                bgcolor: PURPLE_MAIN,
                color: "white",
                borderRadius: 1.5,
                px: 1.5,
                py: 0.5,
                flexShrink: 0,
                "&:hover": { bgcolor: PURPLE_DARK },
              }}
            >
              <Search fontSize="small" />
            </IconButton>
          </Box>
        </BentoCard>

        {/* Results */}
        {trackingIds.length > 0 && (
          <BentoCard sx={{ mt: "22px" }} bodySx={{ p: 0 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              px={2}
              pt={1.25}
              pb={0.5}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    bgcolor: PURPLE_MAIN,
                    flexShrink: 0,
                    animation: "pulse 2s infinite",
                    "@keyframes pulse": {
                      "0%, 100%": { opacity: 1 },
                      "50%": { opacity: 0.5 },
                    },
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {isMultiple
                    ? t("trackingPage.trackingNumbersCount", { count: trackingIds.length })
                    : <>{t("trackingPage.trackingLabel")}: <strong>{trackingIds[0]}</strong></>}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => handleSearch(inputValue)} sx={{ color: "text.secondary" }}>
                <Refresh fontSize="small" />
              </IconButton>
            </Box>
            <Divider sx={{ borderColor: DIVIDER }} />

            {isSingle && (
              <Box
                sx={{
                  "& > div > .MuiPaper-root": {
                    maxWidth: "100%",
                    borderRadius: 0,
                    boxShadow: "none",
                    backgroundColor: BG_TINT,
                    mt: 0,
                  },
                }}
              >
                <TrackingDetail trackingNumber={trackingIds[0]} />
              </Box>
            )}

            {isMultiple && (
              <Box>
                {trackingIds.map((id, i) => (
                  <React.Fragment key={id}>
                    <TrackingSummaryItem trackingId={id} masked={false} />
                    {i < trackingIds.length - 1 && <Divider sx={{ borderColor: DIVIDER }} />}
                  </React.Fragment>
                ))}
              </Box>
            )}
          </BentoCard>
        )}
      </Box>

      <Scanner
        open={open}
        setOpen={setOpen}
        closeAfterResult={true}
        onDecode={(result) => {
          if (result) {
            const clean = getScanText(result);
            const current = parseInput(inputValue);
            const merged = [...new Set([...current, clean])].slice(0, MAX_TRACKING);
            const newInput = merged.join(", ");
            setInputValue(newInput);
            setOpen(false);
            handleSearch(newInput);
            selectAndFocus("trackingNumber");
          }
        }}
      />
    </PageShell>
  );
};

export default Tracking;
